import { useQuery } from "@apollo/client";
import { GridLegacy as Grid, useTheme } from "@mui/material";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { gql } from "generated-graphql";
import { ActivityStatus, TierIiReportKind } from "generated-graphql/graphql";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AssignmentManagementTableFilterKeys } from "../AssignmentManagerTable/useAssignmentManagerOmnisearch";
import { FULFILLMENT_DEFAULT_REPORT_KINDS } from "../fulfillmentDefaults";
import { WorkflowOverviewActivitiesByAssigneesCard } from "./WorkflowOverviewActivitiesByAssigneesCard";
import { WorkflowOverviewKeyMetrics } from "./WorkflowOverviewKeyMetrics";
import { WorkflowOverviewPieChart } from "./WorkflowOverviewPieChart";

const WORKFLOW_OVERVIEW_STATS = gql(`
    query WorkflowOverviewStats($year: Int!, $reportKinds: [TierIIReportKind!]!) {
      workflowOverviewSeasonSummary(year: $year, reportKinds: $reportKinds) {
        year
        workflowsCountError
        workflowsCountErrorUntagged
        workflowsCountErrorCompliance
        workflowsCountErrorEngineering
        workflowsCountWaitForAll
        workflowsCountHuman
        workflowsCountHumanUnassigned
        workflowsCountInProgress
        workflowsCountAutomationInProgress
        workflowsCountAutomationError
        workflowsCountGeppettoInFlight
        workflowsCountGeppettoError
        workflowsByType {
          type
          count
        }
        activitiesByStatus {
          type
          count
        }
        activitiesByType {
          type
          count
        }
        errorsByState {
          type
          count
        }
        staffSummaries {
          id
          name
          reportCount
          completedCount
        }
      }
    }
  `);

export function WorkflowOverviewStats() {
  const theme = useTheme();
  const navigate = useNavigate();

  // Use same pattern as Summary - useOmnisearchDatagrid with createUrlFromOmnisearchKeyValues
  const { omnisearch, getOmnisearchValue, createUrlFromOmnisearchKeyValues } =
    useOmnisearchDatagrid({
      persistenceKey: "workflow-overview-stats",
      isURLDriven: true,
    });

  const yearFilterValue = getOmnisearchValue("reportingYear");
  const reportKindValue = getOmnisearchValue("reportKind");

  const [reportKinds, setReportKinds] = useState(
    (reportKindValue?.split("|") as TierIiReportKind[]) ?? FULFILLMENT_DEFAULT_REPORT_KINDS
  );
  const [year, setYear] = useState(
    yearFilterValue ? parseInt(yearFilterValue) : currentTierIIReportingYear
  );

  const { data, loading } = useQuery(WORKFLOW_OVERVIEW_STATS, {
    fetchPolicy: "network-only",
    variables: {
      year,
      reportKinds,
    },
  });

  useEffect(() => {
    setReportKinds(
      (reportKindValue?.split("|") as TierIiReportKind[]) ?? FULFILLMENT_DEFAULT_REPORT_KINDS
    );
    setYear(yearFilterValue ? parseInt(yearFilterValue) : currentTierIIReportingYear);
  }, [reportKindValue, yearFilterValue, omnisearch]);

  // Navigate to filtered view using createUrlFromOmnisearchKeyValues (same pattern as Summary)
  const handleWorkflowTypeClick = useCallback(
    (type: string) => {
      const url = createUrlFromOmnisearchKeyValues({
        keyValues: [["workflowType", type]],
        keysToKeep: ["reportingYear", "reportKind"],
      });
      navigate(url);
    },
    [navigate, createUrlFromOmnisearchKeyValues]
  );

  const handleActivityStatusClick = useCallback(
    (status: string) => {
      const url = createUrlFromOmnisearchKeyValues({
        keyValues: [[AssignmentManagementTableFilterKeys.ActivityStatus, status]],
        keysToKeep: ["reportingYear", "reportKind"],
      });
      navigate(url);
    },
    [navigate, createUrlFromOmnisearchKeyValues]
  );

  const handleActivityTypeClick = useCallback(
    (type: string) => {
      const url = createUrlFromOmnisearchKeyValues({
        keyValues: [[AssignmentManagementTableFilterKeys.ActivityType, type]],
        keysToKeep: ["reportingYear", "reportKind"],
      });
      navigate(url);
    },
    [navigate, createUrlFromOmnisearchKeyValues]
  );

  const handleErrorsByStateClick = useCallback(
    (state: string) => {
      const url = createUrlFromOmnisearchKeyValues({
        keyValues: [
          [AssignmentManagementTableFilterKeys.State, state],
          [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.Error],
        ],
        keysToKeep: ["reportingYear", "reportKind"],
      });
      navigate(url);
    },
    [navigate, createUrlFromOmnisearchKeyValues]
  );

  return (
    <>
      <Grid item xs={12} marginBottom={theme.spacing(3)}>
        <Grid container spacing={theme.spacing(3)}>
          <Grid item lg={5} xs={12} sx={{ height: "380px" }}>
            <WorkflowOverviewActivitiesByAssigneesCard
              data={data?.workflowOverviewSeasonSummary?.staffSummaries}
              loading={loading}
            />
          </Grid>
          <Grid item lg={7} xs={12}>
            <WorkflowOverviewKeyMetrics
              data={data?.workflowOverviewSeasonSummary}
              loading={loading}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} marginBottom={theme.spacing(3)}>
        <Grid container spacing={theme.spacing(3)}>
          <Grid item lg={3} md={6} xs={12} sx={{ minHeight: "280px" }}>
            <WorkflowOverviewPieChart
              title="Workflow by Type"
              data={data?.workflowOverviewSeasonSummary?.workflowsByType}
              loading={loading}
              onItemClick={handleWorkflowTypeClick}
            />
          </Grid>
          <Grid item lg={3} md={6} xs={12} sx={{ minHeight: "280px" }}>
            <WorkflowOverviewPieChart
              title="Activity by Status"
              data={data?.workflowOverviewSeasonSummary?.activitiesByStatus}
              loading={loading}
              onItemClick={handleActivityStatusClick}
            />
          </Grid>
          <Grid item lg={3} md={6} xs={12} sx={{ minHeight: "280px" }}>
            <WorkflowOverviewPieChart
              title="Activity by Type"
              data={data?.workflowOverviewSeasonSummary?.activitiesByType}
              loading={loading}
              onItemClick={handleActivityTypeClick}
            />
          </Grid>
          <Grid item lg={3} md={6} xs={12} sx={{ minHeight: "280px" }}>
            <WorkflowOverviewPieChart
              title="Errors by State"
              data={data?.workflowOverviewSeasonSummary?.errorsByState}
              loading={loading}
              onItemClick={handleErrorsByStateClick}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
